import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { CONTENT_META, useContent } from "@/content";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Save, Loader2 } from "lucide-react";
import { MobileSizeControl, MobilePreview } from "@/components/admin/MobileSizeControl";

export const SiteContentTab = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const { overrides, refresh } = useContent();
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(null);
  const [mobile, setMobile] = useState({});

  useEffect(() => {
    const v = {};
    CONTENT_META.forEach(m => { v[m.key] = overrides[m.key] ?? m.def; });
    setValues(v);
    setMobile({ ...(overrides._mobile_font_sizes || {}) });
  }, [overrides]);

  const save = async (key) => {
    setSaving(key);
    try {
      await axios.put(`${API}/admin/site-content`, { key, value: values[key], mobile_font_size: mobile[key] || null }, { headers });
      toast.success("Saved — live immediately, no redeploy needed");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setSaving(null); }
  };

  const groups = [...new Set(CONTENT_META.map(m => m.group))];

  return (
    <div className="space-y-6" data-testid="site-content-tab">
      <p className="text-sm text-muted-foreground">Edit and format labels and text across the app with the rich text editor. Changes go live for everyone instantly — no redeploy needed.</p>
      {groups.map(group => (
        <Card key={group} className="border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Manrope']">{group}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {CONTENT_META.filter(m => m.group === group).map(m => {
              const isOverridden = overrides[m.key] !== undefined && overrides[m.key] !== "";
              return (
                <div key={m.key} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{m.label}</span>
                    {isOverridden && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 font-semibold">customized</span>}
                  </div>
                  <RichTextEditor
                    value={values[m.key] ?? ""}
                    onChange={(html) => setValues(prev => ({ ...prev, [m.key]: html }))}
                    minHeight={44}
                    testId={`content-input-${m.key}`}
                  />
                  {mobile[m.key] && <MobilePreview html={values[m.key] ?? ""} size={mobile[m.key]} testId={`content-mobile-preview-${m.key}`} />}
                  <div className="flex items-center justify-end gap-3">
                    <MobileSizeControl value={mobile[m.key]} onChange={(n) => setMobile(prev => ({ ...prev, [m.key]: n }))} testId={`content-mobile-size-${m.key}`} />
                    <Button size="sm" onClick={() => save(m.key)} disabled={saving === m.key} aria-label={`Save ${m.label}`} data-testid={`content-save-${m.key}`}>
                      {saving === m.key ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />} Save
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
